import { Alert, Spin } from 'antd'
import React from 'react'
import styled from 'styled-components/macro'
import {
  HARBOUR_EMPTY_TODAY_COPY,
  HARBOUR_TODAY_SUBTITLE,
  HARBOUR_TODAY_TITLE,
} from 'src/constants/harbour'
import { HARBOUR_TOOLTIPS } from 'src/utils/harbourTooltips'
import {
  getHarbourTodayNudge,
  sortHarbourTodayCards,
} from 'src/utils/harbourToday'
import HarbourCard from './HarbourCard'

/**
 * Today strip — the real Harbour homepage. Overdue then due today.
 * Empty is a one-line call-out so Needs you stays in the first viewport.
 */
const HarbourToday = ({
  cards = [],
  loading = false,
  error = null,
  now,
  startBusyVenueId = null,
  onRetry,
  onSelectVenue,
  onStartWork,
}) => {
  const sorted = sortHarbourTodayCards(cards)
  const nudge = getHarbourTodayNudge(sorted)
  const empty = !loading && !error && sorted.length === 0

  return (
    <Section
      data-testid='harbour-today'
      data-empty={empty ? 'true' : 'false'}
      title={HARBOUR_TOOLTIPS.today}
    >
      <Header>
        <Heading>
          <Title>{HARBOUR_TODAY_TITLE}</Title>
          {empty ? (
            <Empty data-testid='harbour-today-empty'>
              {HARBOUR_EMPTY_TODAY_COPY}
            </Empty>
          ) : (
            <Subtitle>{HARBOUR_TODAY_SUBTITLE}</Subtitle>
          )}
        </Heading>
        {empty ? null : (
          <Count data-testid='harbour-today-count'>{sorted.length}</Count>
        )}
      </Header>

      {nudge ? <Nudge data-testid='harbour-today-nudge'>{nudge}</Nudge> : null}

      {error ? (
        <Alert
          type='error'
          showIcon
          message={error}
          action={
            onRetry ? (
              <button
                type='button'
                onClick={onRetry}
                data-testid='harbour-today-retry'
              >
                Retry
              </button>
            ) : null
          }
        />
      ) : null}

      {loading && sorted.length === 0 ? (
        <Loading data-testid='harbour-today-loading'>
          <Spin size='small' />
        </Loading>
      ) : null}

      {sorted.length > 0 ? (
        <Strip>
          {sorted.map(card => (
            <HarbourCard
              key={card.venueId}
              card={card}
              now={now}
              startBusy={startBusyVenueId === card.venueId}
              onSelectVenue={onSelectVenue}
              onStartWork={onStartWork}
            />
          ))}
        </Strip>
      ) : null}
    </Section>
  )
}

const Section = styled.section`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.sm || '8px'};

  &[data-empty='true'] {
    gap: 0;
  }
`

const Header = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
`

const Heading = styled.div`
  min-width: 0;
`

const Title = styled.h3`
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  color: ${({ theme }) => theme.colours.broccoliGreen || '#1a1a1a'};
`

const Subtitle = styled.p`
  margin: 4px 0 0;
  font-size: 12px;
  color: ${({ theme }) => theme.colours.oliveGrey || '#888'};
`

const Count = styled.span`
  padding: 2px 8px;
  border-radius: 999px;
  background: #f4f8f6;
  color: #2d5a1e;
  font-size: 12px;
  font-weight: 700;
`

const Nudge = styled.div`
  padding: 8px 12px;
  border-radius: 8px;
  background: #fdf6f6;
  border: 1px solid #e8c4c3;
  color: #a94442;
  font-size: 13px;
  font-weight: 600;
`

const Loading = styled.div`
  min-height: 48px;
`

const Empty = styled.p`
  margin: 4px 0 0;
  color: ${({ theme }) => theme.colours.stormGrey || '#666'};
  font-size: 13px;
`

const Strip = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing.sm || '8px'};
`

export default HarbourToday
